"use client";

import { useRef, useState, useEffect } from "react";

const SPEEDS = [1, 1.5, 2, 0.5] as const;
const BAR_COUNT = 90;

function formatTime(secs: number): string {
  if (!isFinite(secs)) return "0:00";
  const m = Math.floor(secs / 60);
  const s = Math.floor(secs % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
}

function decodeWaveform(src: string, bars: number): Promise<number[]> {
  const AudioCtx = window.AudioContext ?? ((window as unknown as Record<string, unknown>)["webkitAudioContext"] as typeof AudioContext);
  const ctx = new AudioCtx();
  return fetch(src)
    .then((r) => r.arrayBuffer())
    .then((buf) => ctx.decodeAudioData(buf))
    .then((audioBuffer) => {
      const data = audioBuffer.getChannelData(0);
      const blockSize = Math.floor(data.length / bars);
      const rms = Array.from({ length: bars }, (_, i) => {
        let sum = 0;
        const start = i * blockSize;
        for (let j = start; j < start + blockSize; j++) sum += data[j] ** 2;
        return Math.sqrt(sum / blockSize);
      });
      const peak = Math.max(...rms, 1e-6);
      // Scale to 10–95%, so even quiet passages have a visible bar
      return rms.map((v) => 10 + (v / peak) * 85);
    })
    .finally(() => ctx.close());
}

export function AudioPlayer({ src, filename }: { src: string; filename: string }) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const waveformRef = useRef<HTMLDivElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [speedIdx, setSpeedIdx] = useState(0);
  const [failed, setFailed] = useState(false);
  const [waveform, setWaveform] = useState<number[] | null>(null);
  // Two separate jobs, deliberately not one ref: `decodedSrcRef` below guards
  // against kicking off a second decode (never read during render), while
  // `waveformLoading` drives the shimmer and so has to be state — setting a
  // ref doesn't re-render, which is why seeking before metadata arrived used
  // to start the decode without ever showing the loading bars.
  const [waveformLoading, setWaveformLoading] = useState(false);
  // Holds the src we've already kicked off a decode for, rather than a bare
  // boolean: comparing against the current src makes the guard invalidate
  // itself when the track changes, so nothing has to reset it.
  const decodedSrcRef = useRef<string | null>(null);

  function loadWaveform() {
    if (decodedSrcRef.current === src) return;
    const requestedSrc = src;
    decodedSrcRef.current = requestedSrc;
    setWaveformLoading(true);
    // A decode outlives the track that started it: if `src` changed while this
    // was in flight, both the bars and the shimmer belong to the old track, and
    // writing either would corrupt the new one's state.
    const isCurrent = () => decodedSrcRef.current === requestedSrc;
    decodeWaveform(requestedSrc, BAR_COUNT)
      .then((bars) => {
        if (isCurrent()) setWaveform(bars);
      })
      .catch(() => {})
      .finally(() => {
        if (isCurrent()) setWaveformLoading(false);
      });
  }

  // Reset the decoded waveform when the track changes. Adjusted during render
  // rather than in an effect so a new `src` never paints one frame carrying the
  // previous track's bars.
  const [prevSrc, setPrevSrc] = useState(src);
  if (prevSrc !== src) {
    setPrevSrc(src);
    setWaveform(null);
    setWaveformLoading(false);
  }

  // Disown a decode still running for the previous track, so the isCurrent()
  // guards above see it as stale. In an effect cleanup rather than in the
  // render adjustment: writing a ref during render is the thing the refs rule
  // forbids, and the cleanup fires on the same `src` change anyway — before
  // anything can start a decode for the new track.
  useEffect(() => {
    return () => {
      decodedSrcRef.current = null;
    };
  }, [src]);

  useEffect(() => {
    const audio = new Audio(src);
    audioRef.current = audio;

    const onTimeUpdate = () => setCurrentTime(audio.currentTime);
    const onDurationChange = () => {
      if (isFinite(audio.duration)) setDuration(audio.duration);
    };
    const onEnded = () => setIsPlaying(false);
    const onError = () => setFailed(true);

    audio.addEventListener("timeupdate", onTimeUpdate);
    audio.addEventListener("durationchange", onDurationChange);
    audio.addEventListener("loadedmetadata", onDurationChange);
    audio.addEventListener("ended", onEnded);
    audio.addEventListener("error", onError);

    return () => {
      audio.pause();
      audio.removeEventListener("timeupdate", onTimeUpdate);
      audio.removeEventListener("durationchange", onDurationChange);
      audio.removeEventListener("loadedmetadata", onDurationChange);
      audio.removeEventListener("ended", onEnded);
      audio.removeEventListener("error", onError);
    };
  }, [src]);

  if (failed) {
    return <audio controls src={src} className="w-full mt-1" />;
  }

  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;
    loadWaveform();
    if (isPlaying) {
      audio.pause();
      setIsPlaying(false);
    } else {
      audio.play().catch(() => setFailed(true));
      setIsPlaying(true);
    }
  };

  const cycleSpeed = () => {
    const audio = audioRef.current;
    const next = (speedIdx + 1) % SPEEDS.length;
    setSpeedIdx(next);
    if (audio) audio.playbackRate = SPEEDS[next];
  };

  const seek = (e: React.MouseEvent<HTMLDivElement>) => {
    const audio = audioRef.current;
    loadWaveform();
    if (!audio || !duration || !waveformRef.current) return;
    const rect = waveformRef.current.getBoundingClientRect();
    const frac = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    audio.currentTime = frac * duration;
    setCurrentTime(frac * duration);
  };

  const progress = duration > 0 ? currentTime / duration : 0;
  const isLoading = waveformLoading && !waveform;

  const WAVE_HEIGHTS = [30, 50, 70, 55, 85, 65, 40, 75, 55, 80, 45, 90, 60, 75, 50, 65, 35, 80, 55, 85];
  const bars = waveform ?? Array.from({ length: BAR_COUNT }, (_, i) => WAVE_HEIGHTS[i % WAVE_HEIGHTS.length]);

  return (
    <div className="bg-indigo-600 rounded-xl p-3 w-full select-none">
      <p className="text-white text-xs font-medium truncate mb-2">{filename}</p>

      <div className="flex items-center gap-3">
        <button
          onClick={togglePlay}
          className="shrink-0 w-9 h-9 rounded-full bg-white flex items-center justify-center hover:bg-indigo-50 transition-colors"
          aria-label={isPlaying ? "Pause" : "Afspil"}
        >
          {isPlaying ? (
            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" className="text-indigo-600">
              <rect x="5" y="4" width="4" height="16" rx="1" />
              <rect x="15" y="4" width="4" height="16" rx="1" />
            </svg>
          ) : (
            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" className="text-indigo-600 ml-0.5">
              <path d="M5 3.868v16.264c0 .92 1.033 1.457 1.8.938l12.298-8.132a1.1 1.1 0 0 0 0-1.876L6.8 2.93C6.033 2.41 5 2.947 5 3.868z" />
            </svg>
          )}
        </button>

        <div
          ref={waveformRef}
          onClick={seek}
          className="flex-1 flex items-center justify-between h-8 cursor-pointer"
        >
          {bars.map((h, i) => (
            <div
              key={i}
              className="rounded-full bg-white"
              style={{
                width: "2px",
                height: `${h}%`,
                opacity: isLoading ? 0.5 : (i / BAR_COUNT < progress ? 0.95 : 0.35),
                animation: isLoading ? `audioWave 1.2s ease-in-out ${(i % 10) * 0.06}s infinite alternate` : undefined,
              }}
            />
          ))}
        </div>
      </div>

      <div className="flex items-center justify-between mt-2">
        <span className="text-white text-xs tabular-nums">{formatTime(currentTime)}</span>
        <button
          onClick={cycleSpeed}
          className="text-white text-xs font-medium bg-indigo-500 hover:bg-indigo-400 transition-colors rounded px-1.5 py-0.5"
        >
          {SPEEDS[speedIdx]}×
        </button>
        <span className="text-white text-xs tabular-nums">{formatTime(duration)}</span>
      </div>
    </div>
  );
}
